/* ============================================================
 * 字典缓存
 *
 * `/app/apply/creator/dict` / `/app/apply/enterprise/dict` /
 * `/app/order/dict` 是几乎不变的数据（Java 硬编码枚举）。
 * 每次进入页面都拉一次既浪费带宽，也不友好。
 *
 * 策略：sessionStorage 缓存 + 软过期（10 分钟内复用）。
 * - 同 session 内直接读缓存
 * - 跨 session 强制重新拉
 * - 后端上线新枚举时只能清缓存或换 version key
 *
 * 内部存储 `{ value, fetchedAt }`，key 区分业务，避免跨业务错乱。
 * - KEY_ORDER_DICT 用 `_v1` 版本后缀，便于后端字段调整时强刷缓存
 * ============================================================ */

import type { CreatorDict, EnterpriseDict } from '@/types/apply'
import type { OrderDictVO, OrderSkillDictItem } from '@/types/order'
import type { JobDictVO } from '@/types/job'

const TTL_MS = 10 * 60 * 1000
const KEY_CREATOR = 'xuntu:dict:creator'
const KEY_ENTERPRISE = 'xuntu:dict:enterprise'
const KEY_ORDER_SKILL = 'xuntu:dict:order_skill'
const KEY_ORDER_DICT = 'xuntu:dict:order_dict_v1'
const KEY_JOB_DICT = 'xuntu:dict:job_dict_v1'

interface CachedEntry<T> {
  value: T
  fetchedAt: number
}

function read<T>(key: string): T | null {
  try {
    if (typeof sessionStorage === 'undefined') return null
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedEntry<T>
    if (Date.now() - parsed.fetchedAt > TTL_MS) return null
    return parsed.value
  } catch {
    return null
  }
}

function write<T>(key: string, value: T): void {
  try {
    if (typeof sessionStorage === 'undefined') return
    sessionStorage.setItem(
      key,
      JSON.stringify({ value, fetchedAt: Date.now() } satisfies CachedEntry<T>),
    )
  } catch {
    // sessionStorage 满了或被禁，静默忽略
  }
}

function remove(key: string): void {
  try {
    if (typeof sessionStorage === 'undefined') return
    sessionStorage.removeItem(key)
  } catch {
    // 静默忽略
  }
}

export function readCreatorDict(): CreatorDict | null {
  return read<CreatorDict>(KEY_CREATOR)
}

export function writeCreatorDict(value: CreatorDict): void {
  write(KEY_CREATOR, value)
}

export function readEnterpriseDict(): EnterpriseDict | null {
  return read<EnterpriseDict>(KEY_ENTERPRISE)
}

export function writeEnterpriseDict(value: EnterpriseDict): void {
  write(KEY_ENTERPRISE, value)
}

export function readOrderSkillDict(): OrderSkillDictItem[] | null {
  return read<OrderSkillDictItem[]>(KEY_ORDER_SKILL)
}

export function writeOrderSkillDict(value: OrderSkillDictItem[]): void {
  write(KEY_ORDER_SKILL, value)
}

export function removeOrderSkillDict(): void {
  remove(KEY_ORDER_SKILL)
}

export function readOrderDict(): OrderDictVO | null {
  return read<OrderDictVO>(KEY_ORDER_DICT)
}

export function writeOrderDict(value: OrderDictVO): void {
  write(KEY_ORDER_DICT, value)
}

export function removeOrderDict(): void {
  remove(KEY_ORDER_DICT)
}

/* 招聘模块：岗位类型 / 学历 / 经验 / 城市 四个下拉。
 * 值域实测稳定（4/5/6/11 项），但 label 由后端维护（例：FRESH 实际是「应届生」），
 * 所以必须缓存后端返回的 label，不能在前端硬编码。 */
export function readJobDict(): JobDictVO | null {
  return read<JobDictVO>(KEY_JOB_DICT)
}

export function writeJobDict(value: JobDictVO): void {
  write(KEY_JOB_DICT, value)
}

export function removeJobDict(): void {
  remove(KEY_JOB_DICT)
}
